import { KPI_DIMENSION_LABELS, KPI_MEASURE_LABELS } from "./kpiCatalogSeed";
import { KPI_LIBRARY } from "../../Resource-Selection/strategyConstants";
import { scoringSpecKpiKey } from "../../Resource-Selection/parityConfigMappers";

const asArray = (value) => (Array.isArray(value) ? value : []);

/** Lane base KPI rows in carrier_kpi (backend {@code lane_scoped} catalog bases). */
export function isLaneScopedBaseKpi(baseOrKpiCode) {
  if (baseOrKpiCode && typeof baseOrKpiCode === "object") {
    if (baseOrKpiCode.laneScoped === true) return true;
    const code = String(baseOrKpiCode.kpiCode ?? baseOrKpiCode.kpi_code ?? "").trim();
    return code.startsWith("lane_level_");
  }
  return String(baseOrKpiCode || "").trim().startsWith("lane_level_");
}

/** Whether KPI Library "Calculate" should be enabled for this base catalog entry. */
export function canCalculateCarrierKpisForBase(base) {
  return Boolean(base);
}

/** True when resource selection ranks using corridor lane_key rows (lane_level_* catalog bases only). */
export function usesLaneRankingGrain(baseOrKpiCode) {
  return isLaneScopedBaseKpi(baseOrKpiCode);
}

/** Human-readable ranking grain for selection variants (not stored on variant — backend policy). */
export function getCarrierSelectionGrainInfo(base) {
  if (usesLaneRankingGrain(base)) {
    return {
      rankingGrain: "lane",
      rankingGrainLabel: "Lane (corridor)",
      rankingGrainDetail:
        "Lane-scoped base KPI: stored and ranked by corridor (carrier_kpi.lane_key). Matching lane row is used when present; otherwise that weight contributes neutral factor 1.0. Calculate via sync-lane-base on the base kpi_code.",
    };
  }
  return {
    rankingGrain: "office",
    rankingGrainLabel: "Office-wide only",
    rankingGrainDetail:
      "Office KPI: selection variants write carrier_kpi with empty lane_key. Ranking uses office-wide values only. For corridor-specific scoring, add a lane_level_* KPI to the strategy weights instead.",
  };
}

export function formatVariantTenantScope(officeScoped) {
  return officeScoped ? "Per shipper office" : "Global (all offices)";
}

export function normalizeBaseKpi(raw) {
  if (!raw || typeof raw !== "object") return null;
  const kpiCode = raw.kpi_code ?? raw.kpiCode ?? raw.code;
  if (!kpiCode) return null;

  const variants = asArray(raw.selection_variants ?? raw.selectionVariants).map(normalizeSelectionVariant);

  const measureList = asArray(raw.measures);
  const measuresFromObjects = measureList
    .map((m) => (typeof m === "string" ? m : m?.measure_code ?? m?.measureCode))
    .filter(Boolean);
  const measures =
    measuresFromObjects.length > 0
      ? measuresFromObjects
      : asArray(raw.supported_measures ?? raw.supportedMeasures).length
        ? asArray(raw.supported_measures ?? raw.supportedMeasures)
        : ["total_events", "success_events", "rate_pct"];

  const code = String(kpiCode);
  const laneScoped = Boolean(
    raw.lane_scoped ?? raw.laneScoped ?? (code.startsWith("lane_level_") ? true : false)
  );
  const bridgeRaw = raw.bridge_enabled ?? raw.bridgeEnabled;
  const bridgeEnabled =
    bridgeRaw != null ? bridgeRaw !== false && bridgeRaw !== 0 : true;

  return {
    kpiCode: code,
    displayName: raw.display_name ?? raw.displayName ?? kpiCode,
    kind: raw.kind ?? "base",
    category: raw.category ?? "",
    description: raw.description ?? "",
    enabled: raw.enabled !== false,
    laneScoped,
    rollingWindowDays: raw.rolling_window_days ?? raw.rollingWindowDays ?? null,
    bridgeEnabled,
    supportedDimensions: asArray(raw.supported_dimensions ?? raw.supportedDimensions),
    measures,
    lastEtlAt: raw.last_etl_at ?? raw.lastEtlAt ?? null,
    selectionVariants: variants.filter(Boolean),
  };
}

export function normalizeSelectionVariant(raw) {
  if (!raw || typeof raw !== "object") return null;
  const variantCode = raw.variant_code ?? raw.variantCode;
  if (!variantCode) return null;

  const fixed =
    raw.fixed_dimensions ??
    raw.fixedDimensions ??
    {};

  return {
    variantCode: String(variantCode),
    parentKpiCode: raw.parent_kpi_code ?? raw.parentKpiCode ?? "",
    displayName: raw.display_name ?? raw.displayName ?? variantCode,
    scoringWeightKey:
      raw.scoring_weight_key ?? raw.scoringWeightKey ?? scoringSpecKpiKey(variantCode),
    carrierKpiName: raw.carrier_kpi_name ?? raw.carrierKpiName ?? variantCode,
    fixedDimensions: {
      rollingWindowDays:
        fixed.rolling_window_days ?? fixed.rollingWindowDays ?? null,
      officeScoped: Boolean(
        fixed.office_scoped ?? fixed.officeScoped ?? false
      ),
    },
    usedInSelection: raw.used_in_selection ?? raw.usedInSelection ?? true,
    bridgeEnabled: raw.bridge_enabled ?? raw.bridgeEnabled ?? true,
  };
}

export function normalizeKpiCatalogResponse(payload) {
  if (payload && typeof payload === "object" && Array.isArray(payload.kpis)) {
    return payload.kpis.map(normalizeBaseKpi).filter(Boolean);
  }
  const list = asArray(payload?.items ?? payload?.data ?? payload);
  return list.map(normalizeBaseKpi).filter(Boolean);
}

export function normalizeSelectionVariantsResponse(payload) {
  if (payload && typeof payload === "object" && Array.isArray(payload.variants)) {
    return payload.variants.map(normalizeSelectionVariant).filter(Boolean);
  }
  const list = asArray(payload?.items ?? payload?.data ?? payload);
  return list.map(normalizeSelectionVariant).filter(Boolean);
}

export function flattenSelectionVariants(catalog = []) {
  const out = [];
  for (const base of catalog) {
    for (const variant of base.selectionVariants || []) {
      if (variant.usedInSelection === false) continue;
      out.push({
        ...variant,
        parentDisplayName: base.displayName,
        parentEnabled: base.enabled,
      });
    }
  }
  return out;
}

/** Attach flat /api/kpi-selection-variants rows onto catalog bases when the catalog omits them. */
export function mergeSelectionVariantsIntoCatalog(catalog = [], flatVariants = []) {
  if (!Array.isArray(catalog) || !catalog.length) return [];
  if (!Array.isArray(flatVariants) || !flatVariants.length) return catalog;

  const byParent = new Map();
  for (const variant of flatVariants) {
    if (!variant?.variantCode) continue;
    const parent = String(variant.parentKpiCode || "").trim();
    if (!parent) continue;
    if (!byParent.has(parent)) byParent.set(parent, []);
    byParent.get(parent).push(variant);
  }

  return catalog.map((base) => {
    const embedded = base.selectionVariants || [];
    const extra = byParent.get(base.kpiCode) || [];
    if (!extra.length) return base;
    const byCode = new Map();
    for (const variant of [...embedded, ...extra]) {
      if (variant?.variantCode) byCode.set(variant.variantCode, variant);
    }
    return { ...base, selectionVariants: [...byCode.values()] };
  });
}

/** Drag-and-drop library row for Resource Selection (selection variants only). */
export function selectionVariantToLibraryItem(variant, base) {
  const windowDays = variant.fixedDimensions?.rollingWindowDays;
  const subtitle = [
    base?.displayName,
    windowDays ? `${windowDays}d window` : null,
    variant.fixedDimensions?.officeScoped ? "per office" : "global",
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    id: variant.variantCode,
    name: variant.displayName,
    scoringWeightKey: variant.scoringWeightKey,
    variantCode: variant.variantCode,
    parentKpiCode: variant.parentKpiCode || base?.kpiCode,
    subtitle,
    kind: "selection_variant",
  };
}

/** Legacy KPI_LIBRARY fallback when catalog API is unavailable. */
export function legacyKpiLibraryItems() {
  return KPI_LIBRARY.map((row) => ({
    id: row.id,
    name: row.name,
    scoringWeightKey: scoringSpecKpiKey(row.name),
    variantCode: scoringSpecKpiKey(row.name),
    subtitle: "Legacy catalog entry",
    kind: "legacy",
  }));
}

export function buildSelectionLibraryFromCatalog(catalog) {
  const variants = flattenSelectionVariants(catalog);
  if (!variants.length) return [];

  const baseByCode = Object.fromEntries(catalog.map((b) => [b.kpiCode, b]));
  return variants
    .filter((v) => v.parentEnabled !== false)
    .map((v) => {
      const base =
        baseByCode[v.parentKpiCode] ||
        catalog.find((b) =>
          (b.selectionVariants || []).some((sv) => sv.variantCode === v.variantCode)
        );
      return selectionVariantToLibraryItem(v, base);
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Default KPI weight rows for a new strategy from catalog selection variants. */
export function defaultStrategyKpisFromLibrary(selectionLibrary, maxCount = 4) {
  if (!Array.isArray(selectionLibrary) || !selectionLibrary.length) return null;
  const picks = selectionLibrary.slice(0, maxCount);
  const n = picks.length;
  const basePct = Math.floor(100 / n);
  let remainder = 100 - basePct * n;
  return picks.map((item) => {
    const percent = basePct + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    return {
      name: item.name,
      percent,
      scoringWeightKey: item.scoringWeightKey,
      variantCode: item.variantCode,
    };
  });
}

export function dimensionLabel(code) {
  return KPI_DIMENSION_LABELS[code] || String(code || "").replace(/_/g, " ");
}

export function measureLabel(code) {
  return KPI_MEASURE_LABELS[code] || String(code || "").replace(/_/g, " ");
}

export function scoringKeyForKpi(kpi) {
  return kpi?.scoringWeightKey || scoringSpecKpiKey(kpi?.name);
}

export function labelForScoringWeightKey(key, catalog = []) {
  if (!key) return "";
  for (const base of catalog) {
    for (const variant of base.selectionVariants || []) {
      if (variant.scoringWeightKey === key) return variant.displayName;
    }
  }
  const legacy = KPI_LIBRARY.find((row) => scoringSpecKpiKey(row.name) === key);
  if (legacy) return legacy.name;
  return key.replace(/_/g, " ");
}

export function measuresToApiInput(measureCodes) {
  const codes = asArray(measureCodes)
    .map((code) => (typeof code === "string" ? code : code?.measure_code ?? code?.measureCode))
    .filter(Boolean);
  const defaults = new Set(["rate_pct"]);
  return codes.map((measureCode, idx) => ({
    measure_code: measureCode,
    display_label: measureLabel(measureCode),
    sort_order: (idx + 1) * 10,
    is_default: defaults.has(measureCode) || idx === codes.length - 1,
  }));
}

export function baseKpiToAdminUpsertPayload(base, { includeKpiCode = true } = {}) {
  const normalized = base?.kpiCode ? base : normalizeBaseKpi(base);
  if (!normalized) throw new Error("Invalid base KPI payload.");

  const payload = {
    display_name: normalized.displayName,
    description: normalized.description ?? "",
    category: normalized.category || "performance",
    enabled: normalized.enabled !== false,
    bridge_enabled: normalized.bridgeEnabled !== false,
    lane_scoped: Boolean(normalized.laneScoped),
    rolling_window_days: normalized.rollingWindowDays ?? null,
    supported_dimensions: normalized.supportedDimensions ?? [],
    measures: measuresToApiInput(normalized.measures),
  };
  if (includeKpiCode) payload.kpi_code = normalized.kpiCode;
  return payload;
}

export function variantToAdminUpsertPayload(variant, parentKpiCode) {
  const fixed = variant.fixed_dimensions ?? variant.fixedDimensions ?? {};
  const rollingWindowDays =
    variant.rolling_window_days ??
    variant.rollingWindowDays ??
    fixed.rolling_window_days ??
    fixed.rollingWindowDays ??
    90;
  const officeScoped =
    variant.office_scoped ??
    variant.officeScoped ??
    fixed.office_scoped ??
    fixed.officeScoped ??
    true;

  return {
    variant_code: variant.variant_code ?? variant.variantCode,
    parent_kpi_code: parentKpiCode ?? variant.parent_kpi_code ?? variant.parentKpiCode,
    display_name: variant.display_name ?? variant.displayName,
    description: variant.description ?? "",
    scoring_weight_key: variant.scoring_weight_key ?? variant.scoringWeightKey,
    carrier_kpi_name: variant.carrier_kpi_name ?? variant.carrierKpiName,
    rolling_window_days: Number(rollingWindowDays),
    office_scoped: Boolean(officeScoped),
    bridge_enabled: variant.bridge_enabled ?? variant.bridgeEnabled ?? true,
    enabled: variant.enabled !== false,
  };
}

export function variantToApiPayload(variant, parentKpiCode) {
  const admin = variantToAdminUpsertPayload(variant, parentKpiCode);
  return {
    ...admin,
    fixed_dimensions: {
      rolling_window_days: admin.rolling_window_days,
      office_scoped: admin.office_scoped,
    },
    used_in_selection: variant.used_in_selection ?? variant.usedInSelection ?? true,
  };
}

export function baseKpiToApiPayload(base) {
  const admin = baseKpiToAdminUpsertPayload(base);
  return {
    ...admin,
    kind: base.kind ?? "base",
    selection_variants: (base.selectionVariants ?? []).map((v) =>
      variantToApiPayload(v, base.kpiCode)
    ),
  };
}

export function isValidKpiCode(value) {
  return /^[a-z][a-z0-9_]*$/.test(String(value || "").trim());
}

/** Suggest variant field values from base KPI + rolling window. */
export function suggestVariantDefaults(base, windowDays, scoringKeyMode = "auto") {
  const days = Math.max(1, Number(windowDays) || 90);
  const variantCode = `${base.kpiCode}_${days}d`;
  const displayName = `${base.displayName} (${days} days)`;

  let scoringWeightKey = variantCode;
  if (scoringKeyMode === "base") {
    scoringWeightKey = base.kpiCode;
  } else if (scoringKeyMode === "auto" && days === 90) {
    // Legacy configs often use the base KPI code for the 90-day window.
    scoringWeightKey = base.kpiCode;
  }

  return {
    variantCode,
    displayName,
    scoringWeightKey,
    carrierKpiName: variantCode,
    rollingWindowDays: days,
    officeScoped: true,
  };
}

/** First preset window not already used by this base KPI's variants. */
export function pickInitialRollingWindow(base, presets) {
  const used = new Set(
    (base?.selectionVariants || []).map((v) => v.fixedDimensions?.rollingWindowDays)
  );
  for (const preset of presets) {
    if (!used.has(preset.value)) return preset.value;
  }
  return presets[0]?.value ?? 90;
}
